import { action, computed } from 'mobx';
import i18next from 'i18next';
import Alert from 'react-s-alert';

import { analytics } from 'core/analytics';
import { Variables, Input, AppManager } from 'core/index';

import { clientStore } from 'apps/client/app';
import { InitiativeConnManager } from 'apps/initiative/connManager';
import { ProfileConnManager } from 'apps/profile/main/connManager';
import { ProjectConnManager } from 'apps/project/connManager';
import { ThreadConnManager } from 'apps/forum/main/appManager';
import { StoryConnManager } from 'apps/story/main/appManager';
import { PaymentSpaceApp } from 'apps/payment/app';
import { SubscriptionAppManager } from 'apps/payment/subscription/appManager';
import { CollectionConnManager } from 'apps/collection/main/connManager';
import { headerStore } from 'apps/components/header/app';

import {
  ProfileMutation,
  ProfileQuery,
  ProfileFollowingCountQuery,
  FollowingProfilesConnectionQuery,
  FollowingOrganizationsConnectionQuery,
  FollowingProjectsConnectionQuery,
  BanProfileMutation,
} from 'apps/profile/gql';
import { ProfileThreadsQuery } from 'apps/forum/gql';
import { ProfileModel, ProfileApp } from 'apps/profile/app';

export class ProfileAppManager extends AppManager {
  static registryName = 'ProfileAppManager';
  // TODO: dispose of the queries/variables/subManagers when we dispose the appManager
  // TODO: dispose of apps too - for app specific stuff
  // TODO: dispose of Query when we replace it

  constructor(parent, init = {}, options) {
    super(parent, init, options);
    this.AppRef('profile', ProfileApp);

    this.variables = new Variables({
      username: () => this.params.get('profile'),
      // projects
      projectsCursor: () => null,
      projectsCount: () => this.projectsConn.variables.batchSize,
      projectsSortBy: () => this.projectsConn.sortBy,
      // stories
      storiesCursor: () => null,
      storiesCount: () => {
        if (
          this.storiesConn.arrayApp.length > this.storiesConn.initialBatchSize
        ) {
          return this.storiesConn.batchSize;
        }
        return this.storiesConn.initialBatchSize;
      },
      storiesSortBy: () => this.storiesConn.sortBy,
      // threads
      threadCursor: () => this.threadsConn.endCursor,
      threadSortBy: () => this.threadsConn.sortBy,
      threadBatchSize: () => this.threadsConn.batchSize,
      // collections
      collectionsCursor: () => null,
      collectionsCount: () => {
        if (
          this.collectionsConn.arrayApp.length >
          this.collectionsConn.initialBatchSize
        ) {
          return this.collectionsConn.batchSize;
        }
        return this.collectionsConn.initialBatchSize;
      },
      collectionsSortBy: () => this.collectionsConn.sortBy,
      // followers
      followersCursor: () => null,
      followersCount: () => this.followersConn.batchSize,
      followersSortBy: () => this.followersConn.sortBy,
      // followingProfiles
      followingProfilesCursor: () => this.followingProfilesConn.endCursor,
      followingProfilesCount: () => this.followingProfilesConn.batchSize,
      followingProfilesSortBy: () => this.followingProfilesConn.sortBy,
      // followingInitiatives
      followingInitiativesCursor: () => this.followingInitiativesConn.endCursor,
      followingInitiativesCount: () => this.followingInitiativesConn.batchSize,
      followingInitiativesSortBy: () => this.followingInitiativesConn.sortBy,
      // followingProjects
      followingProjectsCursor: () => this.followingProjectsConn.endCursor,
      followingProjectsCount: () =>
        this.followingProjectsConn.variables.batchSize,
      followingProjectsSortBy: () => this.followingProjectsConn.sortBy,
    });

    this.initiativesConn = new InitiativeConnManager(
      this,
      {
        sortBy: 'recent',
      },
      { autoDispose: false },
    );
    this.followersConn = new ProfileConnManager(
      this,
      {
        sortBy: 'recent',
        initialBatchSize: 0,
      },
      { autoDispose: false },
    );
    this.followingProfilesConn = new ProfileConnManager(
      this,
      {
        sortBy: 'recent',
        initialBatchSize: 0,
        loadMoreQuery: FollowingProfilesConnectionQuery,
      },
      { autoDispose: false },
    );
    this.followingInitiativesConn = new InitiativeConnManager(
      this,
      {
        sortBy: 'recent',
        initialBatchSize: 0,
        loadMoreQuery: FollowingOrganizationsConnectionQuery,
      },
      { autoDispose: false },
    );
    this.followingProjectsConn = new ProjectConnManager(
      this,
      {
        sortBy: 'recent',
        initialBatchSize: 10,
        loadMoreQuery: FollowingProjectsConnectionQuery,
      },
      { autoDispose: false },
    );
    this.projectsConn = new ProjectConnManager(
      this,
      {
        sortBy: 'recent',
        initialBatchSize: 6,
        sortOptions: [
          ['recent', i18next.t('Recent')],
          ['followers', i18next.t('Followers')],
          ['contributions', i18next.t('Contributions')],
          ['pageviews', i18next.t('Views')],
        ],
      },
      { autoDispose: false },
    );
    this.storiesConn = new StoryConnManager(
      this,
      {
        sortBy: 'recent',
        initialBatchSize: 4,
      },
      { autoDispose: false },
    );
    this.threadsConn = new ThreadConnManager(
      this,
      {
        sortOptions: [],
        batchSize: 5,
        loadMoreQuery: ProfileThreadsQuery,
      },
      { autoDispose: false },
    );
    this.collectionsConn = new CollectionConnManager(
      this,
      {
        sortBy: 'recent',
      },
      { autoDispose: false },
    );

    this.profileInput = new Input({
      id: () => this.profile.id,
      username: () => this.profile.user.username,
      fullName: () => this.profile.fullName,
      bio: () => this.profile.bio,
      skills: () =>
        this.profile.skills.map(({ id, name, deleted }) => ({
          id,
          name,
          deleted,
        })),
      socialAccounts: () =>
        this.profile.socialAccounts.map(social => ({
          id: social.id,
          accountType: social.accountType,
          accountName: social.accountName,
          deleted: social.deleted,
        })),
      avatar: () => {
        if (this.profile.avatar) {
          return this.profile.avatar.id;
        }
        return null;
      },
    });
    this.variables.onChange(['username'], this.NewUsername);
  }

  @computed get loading() {
    return this.queries.get('profile')?.loading;
  }

  @action NewUsername = () => {
    this.initiativesConn.NewUsername();
    this.projectsConn.NewUsername();
    this.storiesConn.NewUsername();
    this.collectionsConn.NewUsername();
    this.threadsConn.NewVariables();
    this.NewProfileQuery();
  };

  @action NewProfileQuery = () => {
    // dispose old queries via observe
    const q = new ProfileQuery(this, {
      queryKey: 'profile',
      onSysErr: this.onProfileError,
    });
    q.process(profile => ProfileModel.Load(profile), this.ProfileUpdate);
    q.process(this.initiativesConn.process, this.InitiativesUpdate, {
      subPath: 'initiatives',
    });
    q.process(this.followersConn.process, this.FollowersUpdate, {
      subPath: 'followers',
    });
    q.process(
      this.followingProfilesConn.process,
      this.FollowingProfilesUpdate,
      { subPath: 'followingProfiles' },
    );
    q.process(
      this.followingInitiativesConn.process,
      this.FollowingInitiativesUpdate,
      { subPath: 'followingInitiatives' },
    );
    q.process(
      this.followingProjectsConn.process,
      this.FollowingProjectsUpdate,
      { subPath: 'followingProjects' },
    );
    q.process(this.projectsConn.process, this.ProjectsUpdate, {
      subPath: 'contributedProjects',
    });
    q.process(this.storiesConn.process, this.StoriesUpdate, {
      subPath: 'stories',
    });
    q.process(this.collectionsConn.process, this.CollectionsUpdate, {
      subPath: 'collections',
    });
    q.process(this.threadsConn.process, this.ThreadsUpdate, {
      subPath: 'threads',
    });
    q.query();
  };

  @action FollowingUpdate = () => {
    const q = new ProfileFollowingCountQuery(this, {
      queryKey: 'following',
      onSysErr: this.onProfileError,
    });
    q.process(
      result => ProfileModel.Load(result),
      profile => {
        this.profile.followingCount = profile.followingCount;
      },
    );
    q.query();
  };

  @action ProfileUpdate = profileModel => {
    this.profile = profileModel;
    if (this.profile) {
      headerStore.SetPage(profileModel);
    }
  };

  @action InitiativesUpdate = (initiatives, query) => {
    this.initiativesConn.pushResultIfFirst(initiatives, query);
  };

  @action FollowersUpdate = (followers, query) => {
    this.followersConn.pushResultIfFirst(followers, query);
  };

  @action FollowingProfilesUpdate = (followingProfiles, query) => {
    this.followingProfilesConn.pushResultIfFirst(followingProfiles, query);
  };

  @action FollowingInitiativesUpdate = (followingInitiatives, query) => {
    this.followingInitiativesConn.pushResultIfFirst(
      followingInitiatives,
      query,
    );
  };

  @action FollowingProjectsUpdate = (followingProjects, query) => {
    this.followingProjectsConn.pushResultIfFirst(followingProjects, query);
  };

  @action ProjectsUpdate = (projects, query) => {
    this.projectsConn.pushResultIfFirst(projects, query);
  };

  @action StoriesUpdate = (stories, query) => {
    this.storiesConn.pushResultIfFirst(stories, query);
  };

  @action CollectionsUpdate = (collections, query) => {
    this.collectionsConn.pushResultIfFirst(collections, query);
  };

  @action ThreadsUpdate = (threads, query) => {
    this.threadsConn.pushResultIfFirst(threads, query);
  };

  // FIXME This code is repeated at least in apps/initiative/appManager
  @action Subscribe = params => {
    const subscriptionAppManager = new SubscriptionAppManager(this, params);
    const paymentSpace = new PaymentSpaceApp(subscriptionAppManager, params);
    if (!paymentSpace || (paymentSpace && !paymentSpace.preview)) {
      subscriptionAppManager.NewSubscriptionQuery({
        preview: true,
      });
    }
    paymentSpace.ConfirmSubscribe();
  };

  @action SetBanProfile = (profile, banned, options) => {
    const input = new Input({
      username: profile.user?.username.replace('@', ''),
      banned,
    });
    const mutation = BanProfileMutation.New(input, options);
    return mutation;
  };

  @action Consolidate = () => {
    const { profile } = this;
    if (!profile.editFormStore.isValid) {
      profile.editFormStore.alert();
    } else {
      profile.editFormStore.SetPosting(true);
      const options = {
        onSysErr: this.onSaveSysError,
        onUserErr: this.onSaveUserError,
        onSuccess: this.onSaveSuccess,
      };
      this.mutations.push(ProfileMutation.New(this.profileInput, options));
    }
  };

  Cancel = () => {
    this.profile.ResetLocals();
    this.profile.SetEditing(false);
  };

  Save = () => {
    this.profile.SetSaving(true);
    analytics.sendEvent('updated-profile', {}, { ga: true });
    const avatarField = this.profile.editFormStore.fieldStores.get('avatar');
    if (avatarField && avatarField.fileModified) {
      avatarField.getUpdate(this.Consolidate);
    } else {
      this.Consolidate();
    }
  };

  onSaveSysError = () => {
    this.profile?.SetSaving(false);
    Alert.error(
      i18next.t('Hmm... We failed to update your Profile. Please try again.'),
    );
    this.profile?.editFormStore.SetPosting(false);
  };

  onSaveUserError = errs => {
    this.profile?.SetSaving(false);
    this.profile?.editFormStore.SetPosting(false);
    this.profile?.editFormStore.SetServerErrs(errs);
  };

  onSaveSuccess = profile => {
    if (this.profile?.isOwnProfile) {
      const profileModel = ProfileModel.Load(profile);
      clientStore.SetSignedInProfile(profileModel);
    }
    this.profile?.SetEditing(false);
    this.profile?.SetSaving(false);
    this.profile?.ResetLocals();
    this.profile?.editFormStore.SetPosting(false);
    this.profile?.editFormStore.clearDraft();
    Alert.success(i18next.t('Your Profile has been updated successfully'));
  };

  onBanError = error => {
    analytics.logError(error);
    Alert.error(i18next.t('There was an error while banning the user'));
  };

  onBanSuccess = () => {
    Alert.info(i18next.t('User has been banned ⚡'));
  };

  onUnbanSuccess = () => {
    Alert.info(i18next.t('User has been unbanned 🕊️'));
  };

  Unban = () => {
    this.SetBanProfile(this.profile, false, {
      onSysErr: this.onBanError,
      onUserErr: this.onBanError,
      onSuccess: this.onUnbanSuccess,
    });
  };

  Ban = () => {
    this.SetBanProfile(this.profile, true, {
      onSysErr: this.onBanError,
      onUserErr: this.onBanError,
      onSuccess: this.onBanSuccess,
    });
  };

  disposeWhen() {
    return false;
  }
}
